import { useCustomForm, useDebounce } from "@/hooks";
import { mapLocationToString, useLocation } from "@/hooks/useLocation";
import { useGeneratePostDescriptionMutation } from "@/services/modules/post";
import { FileModel } from "@/types/common";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RouteConstant } from "@/routes/routeConstant";
import { createAdvertSchema, CreateAdvertSchemaModel } from "../types/schema";
import { useAddEditAdvertMutation } from "../api/merchant";
import { PostStatusOption } from "@/utils/listOption";

export const useCreateAdvert = () => {
  const { getCurrentLocation } = useLocation();
  const debounceFn = useDebounce();
  const [generatePostDescription] = useGeneratePostDescriptionMutation();
  const {
    register,
    formState,
    setValue,
    handleSubmit,
    watch,
    getValues,
    reset,
  } = useCustomForm<CreateAdvertSchemaModel>({ schema: createAdvertSchema });
  const { files, prompt } = watch();
  const navigate = useNavigate();
  const [addEditAdvert] = useAddEditAdvertMutation();

  useEffect(() => {
    (async () => {
      try {
        const location = await getCurrentLocation();
        if (location) setValue("location", location);
      } catch (err) {}
    })();
  }, []);

  useEffect(() => {
    if (prompt)
      (async () => {
        debounceFn(async () => {
          var result = await generatePostDescription(prompt).unwrap();
          setValue("description", result.description);
        }, 1800);
      })();
  }, [prompt]);

  const handleUploadImg = (name: string, value: FileModel) => {
    setValue("files", [...(files ? files : []), value]);
  };

  const handleRemoveImg = (index: number) => {
    let fileList = files?.slice() ?? [];
    fileList.splice(index, 1);
    setValue("files", fileList);
  };

  async function saveAdvert(
    { location, ...rest }: CreateAdvertSchemaModel,
    isDraft = false
  ) {
    await addEditAdvert({
      ...rest,
      postLocation: mapLocationToString(location),
      postStatus: isDraft ? PostStatusOption.Draft : PostStatusOption.Active,
    }).unwrap();
    reset();
    navigate(RouteConstant.Adverts);
  }

  async function handleDraft(payload: CreateAdvertSchemaModel) {
    await saveAdvert(payload, true);
  }

  async function handlePublish(payload: CreateAdvertSchemaModel) {
    await saveAdvert(payload, true);
  }

  return {
    register,
    formState,
    setValue,
    watch,
    handleSubmit,
    handleDraft,
    handlePublish,
    handleUploadImg,
    handleRemoveImg,
  };
};
